import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getActiveStoreId, setActiveStore } from "@/lib/activeStore";

/**
 * Centralised active-store state for the vendor panel. Every vendor API call carries the active store id
 * (see lib/api.js) and the server validates it; the panel subtree is keyed by `activeId`, so switching
 * remounts every page and no data of the previous store can linger.
 */
const Ctx = createContext(null);
export const useActiveStore = () => useContext(Ctx);

export function ActiveStoreProvider({ children }) {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const { search, pathname } = useLocation();
  const [stores, setStores] = useState(null);       // null = loading
  const [activeId, setActiveId] = useState(() => (user ? getActiveStoreId(user.id) || user.store_id : null));
  const [switching, setSwitching] = useState(false);
  const busy = useRef(false);

  const adopt = useCallback((id, list) => {
    setActiveStore(user.id, id);
    setActiveId(id);
    const s = (list || []).find((x) => x.id === id);
    setUser((u) => (u && s ? { ...u, store_id: id, store_slug: s.slug } : u));
  }, [user?.id, setUser]);  // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    const { data } = await api.get("/vendor/stores");
    setStores(data.stores);
    adopt(data.active_store_id, data.stores);
    return data;
  }, [adopt]);

  // Initial load; also the fallback when the server reports the remembered store is no longer ours.
  useEffect(() => {
    if (!user) return undefined;
    load().catch(() => setStores([]));
    const revoked = () => { load().then(() => toast.info("That store is no longer available. Switched to another of your stores.")).catch(() => {}); };
    window.addEventListener("store:revoked", revoked);
    return () => window.removeEventListener("store:revoked", revoked);
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const switchStore = useCallback(async (id, { go = "/vendor", silent = false } = {}) => {
    if (busy.current || !id) return false;
    if (id === activeId) { if (go) nav(go); return true; }
    busy.current = true;
    setSwitching(true);
    try {
      const { data } = await api.put(`/vendor/stores/${id}/activate`);
      const list = stores || [];
      adopt(data.active_store_id, list.some((s) => s.id === id) ? list : [...list, data.store]);
      setStores((cur) => (cur || []).map((s) => ({ ...s, active: s.id === id })));
      if (go) nav(go);
      if (!silent) toast.success(`Switched to ${data.store.name}`);
      return true;
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail));
      return false;
    } finally { busy.current = false; setSwitching(false); }
  }, [activeId, stores, adopt, nav]);

  // Deep link: /vendor/...?store=<id|slug> opens that store (if it is ours), then drops the parameter.
  useEffect(() => {
    const want = new URLSearchParams(search).get("store");
    if (!want || !stores) return;
    const hit = stores.find((s) => s.id === want || s.slug === want);
    const params = new URLSearchParams(search); params.delete("store");
    const rest = params.toString();
    const clean = pathname + (rest ? `?${rest}` : "");
    if (!hit) { toast.error("You don't have access to that store."); nav(clean, { replace: true }); return; }
    if (hit.id === activeId) { nav(clean, { replace: true }); return; }
    switchStore(hit.id, { go: null, silent: true }).then(() => nav(clean, { replace: true }));
  }, [search, stores]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Called once a new store was created and fully set up: add it to the list, activate it, open its dashboard. */
  const adoptNewStore = useCallback(async (store) => {
    setStores((cur) => [...(cur || []), store]);
    return switchStore(store.id, { go: "/vendor", silent: true });
  }, [switchStore]);

  const value = useMemo(() => ({
    stores: stores || [], loading: stores === null, activeId, switching,
    active: (stores || []).find((s) => s.id === activeId) || null,
    switchStore, adoptNewStore, reload: load,
  }), [stores, activeId, switching, switchStore, adoptNewStore, load]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
